import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatIST, txnLabel } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, Search, Settings2, QrCode, Wallet, KeyRound, Trash2, Upload, AlertTriangle } from "lucide-react";

const empty = { name: "", email: "", mobile: "", password: "", initial_balance: 0, deposit_limit: 100000, withdrawal_limit: 100000, upi_id: "" };

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [manage, setManage] = useState(null); // user being managed
  const [saving, setSaving] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/users", { params: { include_deleted: showDeleted } }).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, [showDeleted]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users", { ...form, initial_balance: Number(form.initial_balance), deposit_limit: Number(form.deposit_limit), withdrawal_limit: Number(form.withdrawal_limit) });
      toast.success("User created");
      setCreateOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!users) return <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>;
  const filtered = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="User Management" desc="Create users, set limits, assign UPI/QR and adjust balances.">
        <Button onClick={() => setCreateOpen(true)} data-testid="add-user-btn" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">
          <Plus className="mr-2 h-4 w-4" /> Add User
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 bg-slate-900/60 border-slate-700 h-10" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-400"><Switch checked={showDeleted} onCheckedChange={setShowDeleted} data-testid="show-deleted-toggle" /> Show deleted accounts</label>
      </div>

      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-slate-400">User</TableHead>
                <TableHead className="text-slate-400">Balance</TableHead>
                <TableHead className="text-slate-400">Reserved</TableHead>
                <TableHead className="text-slate-400">UPI</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Manage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">No users yet. Click “Add User”.</TableCell></TableRow>
              ) : filtered.map((u) => (
                <TableRow key={u.id} className="border-white/5" data-testid={`user-row-${u.id}`}>
                  <TableCell><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></TableCell>
                  <TableCell className="font-mono text-emerald-300">{formatINR(u.balance)}</TableCell>
                  <TableCell className="font-mono text-amber-300">{formatINR(u.reserved)}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{u.upi_id || "—"}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-mono ${u.status === "active" ? "bg-emerald-500/15 text-emerald-300" : u.status === "deleted" ? "bg-slate-500/20 text-slate-400" : "bg-rose-500/15 text-rose-300"}`}>{u.status}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`manage-user-${u.id}`} onClick={() => setManage(u)} className="border-slate-700 bg-transparent hover:bg-white/5">
                      <Settings2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#121a28] border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Create New User</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name"><Input data-testid="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
            <Field label="Email"><Input data-testid="cu-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
            <Field label="Mobile"><Input data-testid="cu-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
            <Field label="Password"><Input data-testid="cu-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
            <Field label="Initial Balance (₹)"><Input data-testid="cu-balance" type="number" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
            <Field label="UPI ID (optional)"><Input data-testid="cu-upi" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
            <Field label="Deposit Limit (₹)"><Input data-testid="cu-dep-limit" type="number" value={form.deposit_limit} onChange={(e) => setForm({ ...form, deposit_limit: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
            <Field label="Withdrawal Limit (₹)"><Input data-testid="cu-wd-limit" type="number" value={form.withdrawal_limit} onChange={(e) => setForm({ ...form, withdrawal_limit: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-slate-700 bg-transparent">Cancel</Button>
            <Button onClick={create} disabled={saving || !form.name || !form.email || !form.password} data-testid="cu-submit" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {manage && <ManageDialog user={manage} onClose={() => setManage(null)} onChanged={load} />}
    </div>
  );
}

function Field({ label, children }) {
  return (<div><Label className="text-slate-300 text-sm">{label}</Label><div className="mt-1.5">{children}</div></div>);
}

function ManageDialog({ user, onClose, onChanged }) {
  const [u, setU] = useState(user);
  const [edit, setEdit] = useState({ name: user.name, mobile: user.mobile, deposit_limit: user.deposit_limit, withdrawal_limit: user.withdrawal_limit });
  const [upi, setUpi] = useState(user.upi_id || "");
  const [adj, setAdj] = useState({ direction: "credit", amount: "", reason: "" });
  const [dep, setDep] = useState({ amount: "", utr: "", remark: "" });
  const [pwd, setPwd] = useState("");
  const [txns, setTxns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmDep, setConfirmDep] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [depKey, setDepKey] = useState(() => crypto.randomUUID());
  const deleted = u.status === "deleted";

  const reloadUser = useCallback(async () => {
    const r = await api.get(`/admin/users/${user.id}`); setU(r.data); onChanged();
  }, [user.id, onChanged]);

  useEffect(() => { api.get(`/admin/users/${user.id}/transactions`).then((r) => setTxns(r.data)).catch(() => {}); }, [user.id]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await reloadUser(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveDetails = () => run(() => api.put(`/admin/users/${user.id}`, { ...edit, deposit_limit: Number(edit.deposit_limit), withdrawal_limit: Number(edit.withdrawal_limit) }), "Details updated");
  const toggleStatus = (checked) => run(() => api.patch(`/admin/users/${user.id}/status`, { status: checked ? "active" : "suspended" }), "Status updated");
  const saveUpi = () => { const fd = new FormData(); fd.append("upi_id", upi); return run(() => api.put(`/admin/users/${user.id}/upi`, fd), "UPI updated"); };
  const uploadQr = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    run(() => api.post(`/admin/users/${user.id}/qr`, fd), "QR uploaded");
  };
  const removeQr = () => run(() => api.delete(`/admin/users/${user.id}/qr`), "QR removed");
  const doAdjust = () => run(() => api.post(`/admin/users/${user.id}/adjust`, { ...adj, amount: Number(adj.amount) }).then(() => setAdj({ direction: "credit", amount: "", reason: "" })), "Balance adjusted");
  const doDeposit = async () => {
    await run(() => api.post(`/admin/users/${user.id}/deposit`, { ...dep, amount: Number(dep.amount), idempotency_key: depKey }).then(() => { setDep({ amount: "", utr: "", remark: "" }); setDepKey(crypto.randomUUID()); }), "Deposit credited");
    setConfirmDep(false);
  };
  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/admin/users/${user.id}`); toast.success("User deleted — login revoked, records retained"); onChanged(); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const resetPwd = () => run(() => api.post(`/admin/users/${user.id}/reset-password`, { new_password: pwd }).then(() => setPwd("")), "Password reset");

  return (
    <>
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121a28] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        {deleted && <div className="rounded-lg border border-slate-500/30 bg-slate-500/10 p-3 text-sm text-slate-300" data-testid="deleted-banner">This account was deleted on {formatIST(u.deleted_at)}. Financial history is retained for audit (read-only).</div>}
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">{u.name} <span className="text-sm font-normal text-slate-500">· {u.email}</span></DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-3 mb-2">
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2"><div className="text-xs text-slate-400">Available</div><div className="font-mono font-semibold text-emerald-300">{formatINR(u.balance)}</div></div>
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2"><div className="text-xs text-slate-400">Reserved</div><div className="font-mono font-semibold text-amber-300">{formatINR(u.reserved)}</div></div>
          <div className="rounded-lg bg-slate-500/10 border border-white/10 px-3 py-2 flex items-center gap-3">
            <div><div className="text-xs text-slate-400">Active</div><div className="font-mono text-sm">{u.status}</div></div>
            <Switch checked={u.status === "active"} onCheckedChange={toggleStatus} disabled={deleted} data-testid="toggle-status" />
          </div>
        </div>

        <Tabs defaultValue="details">
          <TabsList className="bg-slate-900/60 flex-wrap h-auto">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="payment"><QrCode className="h-4 w-4 mr-1" />UPI/QR</TabsTrigger>
            <TabsTrigger value="balance"><Wallet className="h-4 w-4 mr-1" />Balance</TabsTrigger>
            <TabsTrigger value="security"><KeyRound className="h-4 w-4 mr-1" />Security</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 pt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-name" className="bg-slate-900/60 border-slate-700" /></Field>
              <Field label="Mobile"><Input value={edit.mobile} onChange={(e) => setEdit({ ...edit, mobile: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
              <Field label="Deposit Limit (₹)"><Input type="number" value={edit.deposit_limit} onChange={(e) => setEdit({ ...edit, deposit_limit: e.target.value })} data-testid="edit-dep-limit" className="bg-slate-900/60 border-slate-700" /></Field>
              <Field label="Withdrawal Limit (₹)"><Input type="number" value={edit.withdrawal_limit} onChange={(e) => setEdit({ ...edit, withdrawal_limit: e.target.value })} className="bg-slate-900/60 border-slate-700" /></Field>
            </div>
            <Button onClick={saveDetails} disabled={busy} data-testid="save-details" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Save Details</Button>
          </TabsContent>

          <TabsContent value="payment" className="space-y-4 pt-4">
            <Field label="UPI ID"><div className="flex gap-2"><Input value={upi} onChange={(e) => setUpi(e.target.value)} data-testid="edit-upi" placeholder="name@bank" className="bg-slate-900/60 border-slate-700" /><Button onClick={saveUpi} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Save</Button></div></Field>
            <div className="rounded-lg border border-white/10 bg-black/20 p-4">
              <div className="text-sm text-slate-300 mb-2">QR Code {u.qr_path ? <span className="text-emerald-400 text-xs">· uploaded</span> : <span className="text-slate-500 text-xs">· auto-generated from UPI</span>}</div>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={uploadQr} data-testid="qr-upload-input" />
                  <span className="inline-flex items-center gap-2 rounded-md bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 px-3 py-2 text-sm hover:bg-cyan-500/25"><Upload className="h-4 w-4" /> Upload QR Image</span>
                </label>
                {u.qr_path && <Button variant="outline" onClick={removeQr} disabled={busy} className="border-rose-500/30 text-rose-300 bg-transparent hover:bg-rose-500/10"><Trash2 className="h-4 w-4 mr-1" />Remove</Button>}
              </div>
              <p className="mt-2 text-xs text-slate-500">If no image is uploaded, a UPI QR is auto-generated for the user from their UPI ID.</p>
            </div>
          </TabsContent>

          <TabsContent value="balance" className="space-y-5 pt-4">
            <div className="rounded-lg border border-white/10 p-4 space-y-3">
              <div className="text-sm font-semibold text-slate-200">Manual Adjustment (reason required)</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <select value={adj.direction} onChange={(e) => setAdj({ ...adj, direction: e.target.value })} data-testid="adj-direction" className="h-10 rounded-md bg-slate-900/60 border border-slate-700 px-3 text-sm">
                  <option value="credit">Credit (+)</option><option value="debit">Debit (−)</option>
                </select>
                <Input type="number" placeholder="Amount ₹" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} data-testid="adj-amount" className="bg-slate-900/60 border-slate-700" />
                <Input placeholder="Reason" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} data-testid="adj-reason" className="bg-slate-900/60 border-slate-700" />
              </div>
              <Button onClick={doAdjust} disabled={busy || !adj.amount || !adj.reason} data-testid="adj-submit" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Apply Adjustment</Button>
            </div>
            <div className="rounded-lg border border-white/10 p-4 space-y-3">
              <div className="text-sm font-semibold text-slate-200">Manual Deposit (instant credit)</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Input type="number" placeholder="Amount ₹" value={dep.amount} onChange={(e) => setDep({ ...dep, amount: e.target.value })} data-testid="mdep-amount" className="bg-slate-900/60 border-slate-700" />
                <Input placeholder="UTR / Ref" value={dep.utr} onChange={(e) => setDep({ ...dep, utr: e.target.value })} data-testid="mdep-utr" className="bg-slate-900/60 border-slate-700" />
                <Input placeholder="Remark" value={dep.remark} onChange={(e) => setDep({ ...dep, remark: e.target.value })} className="bg-slate-900/60 border-slate-700" />
              </div>
              <Button onClick={() => setConfirmDep(true)} disabled={busy || deleted || !(Number(dep.amount) > 0)} data-testid="mdep-submit" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Add Deposit</Button>
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-4 pt-4">
            <Field label="Reset Password"><div className="flex gap-2"><Input value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-pwd-input" placeholder="New password" className="bg-slate-900/60 border-slate-700" /><Button onClick={resetPwd} disabled={busy || pwd.length < 4} data-testid="reset-pwd-btn" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">Reset</Button></div></Field>
            {!deleted && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4">
                <div className="text-sm font-semibold text-rose-300 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Danger Zone</div>
                <p className="text-xs text-slate-400 mt-1">Deactivates the account and revokes login. Transactions, ledger, UTRs and disputes are retained.</p>
                <Button onClick={() => setConfirmDel(true)} data-testid="delete-user-btn" variant="outline" className="mt-3 border-rose-500/40 text-rose-300 bg-transparent hover:bg-rose-500/10"><Trash2 className="h-4 w-4 mr-1" />Delete User</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="pt-4">
            <div className="max-h-80 overflow-y-auto rounded-lg border border-white/10">
              <Table>
                <TableHeader><TableRow className="border-white/5 hover:bg-transparent"><TableHead className="text-slate-400">Type</TableHead><TableHead className="text-slate-400">Amount</TableHead><TableHead className="text-slate-400">Status</TableHead><TableHead className="text-slate-400">Date</TableHead></TableRow></TableHeader>
                <TableBody>
                  {txns.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">No transactions.</TableCell></TableRow> :
                    txns.map((t) => (
                      <TableRow key={t.id} className="border-white/5">
                        <TableCell className="capitalize text-sm">{txnLabel(t.type)}{t.direction ? ` (${t.direction})` : ""}</TableCell>
                        <TableCell className="font-mono">{formatINR(t.amount)}</TableCell>
                        <TableCell><StatusBadge status={t.status} /></TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono">{formatIST(t.created_at)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>

    <Dialog open={confirmDep} onOpenChange={setConfirmDep}>
      <DialogContent className="bg-[#121a28] border-white/10 max-w-sm" data-testid="confirm-deposit-dialog">
        <DialogHeader><DialogTitle className="font-display">Confirm Payment Credit</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <Line k="User" v={`${u.name} · ${u.email}`} />
          <Line k="Amount" v={<span className="font-mono text-emerald-300 text-lg font-bold" data-testid="confirm-dep-amount">{formatINR(dep.amount)}</span>} />
          <Line k="Reference / UTR" v={dep.utr || "—"} />
          <Line k="Reason" v={dep.remark || "Manual deposit by admin"} />
          <Line k="Date / time" v={formatIST(new Date().toISOString())} />
          <p className="text-xs text-slate-500 pt-1">The user will be notified (with voice, if enabled) only after the credit is committed.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmDep(false)} className="border-slate-700 bg-transparent">Cancel</Button>
          <Button onClick={doDeposit} disabled={busy} data-testid="confirm-deposit-btn" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Credit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
      <DialogContent className="bg-[#121a28] border-white/10 max-w-sm" data-testid="confirm-delete-dialog">
        <DialogHeader><DialogTitle className="font-display text-rose-300">Delete this user?</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <Line k="Account ID" v={<span className="font-mono text-xs" data-testid="delete-user-id">{u.id}</span>} />
          <Line k="Name" v={u.name} />
          <Line k="Email" v={u.email} />
          <Line k="Balance" v={`${formatINR(u.balance)} (reserved ${formatINR(u.reserved)})`} />
          <p className="text-xs text-slate-400 pt-1">Login will be revoked immediately and personal details anonymised. All financial records stay in the ledger.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmDel(false)} className="border-slate-700 bg-transparent">Cancel</Button>
          <Button onClick={doDelete} disabled={busy} data-testid="confirm-delete-btn" className="bg-rose-500 hover:bg-rose-400 text-white font-semibold">Delete User</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function Line({ k, v }) {
  return <div className="flex justify-between gap-4"><span className="text-slate-500">{k}</span><span className="text-right">{v}</span></div>;
}
